import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Layers,
  HelpCircle,
  Edit3,
  FileText,
  Lock,
  Loader2,
} from "lucide-react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { showToast as toast } from "../../../components/ClassicToast";
import DynamicStageContent from "./stages/DynamicStageContent";
import { stemProjectApi } from "../services/stemProjectApi";
import { fetchProjectProgress, saveStageProgressThunk } from "../redux/stemThunk";
import { markStageCompleted } from "../redux/stemSlice";

export default function StemProjectWorkflow({ project, onBack }) {
  const [stages, setStages] = useState(() =>
    (project?.stages || []).slice().sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0)),
  );
  const [loadingStages, setLoadingStages] = useState(!project?.stages?.length);
  const [activeStageIndex, setActiveStageIndex] = useState(0);

  useEffect(() => {
    if (project?.stages?.length) {
      const sorted = (project.stages || []).slice().sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));
      setStages(sorted);
      setLoadingStages(false);
      return;
    }

    let cancelled = false;
    if (project?.id) {
      setLoadingStages(true);
      stemProjectApi
        .getProjectStages(project.id)
        .then((data) => {
          if (!cancelled) {
            const sorted = (data || []).slice().sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));
            setStages(sorted);
          }
        })
        .catch((err) => {
          console.error("[StemProjectWorkflow] Failed to fetch project stages:", err);
        })
        .finally(() => {
          if (!cancelled) setLoadingStages(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [project?.id, project?.stages]);

  const dispatch = useDispatch();
  const store = useStore();
  const completedStageIds = useSelector((state) => state.stem?.completedStageIds || []);

  // Fetch project progress via Redux on mount and when project id changes
  useEffect(() => {
    if (project?.id) {
      dispatch(fetchProjectProgress(project.id));
    }
  }, [dispatch, project?.id]);

  const activeStage = stages[activeStageIndex] || null;

  const getTypeIcon = (type) => {
    switch ((type || "").toUpperCase()) {
      case "QUESTIONS":
        return <HelpCircle size={12} />;
      case "EDITOR":
        return <Edit3 size={12} />;
      case "DOCUMENT":
        return <FileText size={12} />;
      default:
        return <Layers size={12} />;
    }
  };

  const handleCompleteStage = (stageId) => {
    if (!stageId) return;
    console.log("[STEM WORKFLOW] Completing stage via Redux:", stageId);
    // 1. Immediately and synchronously update Redux state
    dispatch(markStageCompleted(stageId));

    // 2. Persist to backend via Redux thunk
    if (project?.id) {
      dispatch(saveStageProgressThunk({ stageId, projectId: project.id, isComplete: true }))
        .unwrap()
        .catch((err) => {
          console.error("[STEM WORKFLOW] Failed to save stage progress:", err);
          toast.error("Could not save your progress. Please check your connection.");
        });
    }
  };

  const isStageUnlocked = (idx) => {
    // Stage 0 is always accessible
    if (idx === 0) return true;
    const currentCompleted = store.getState().stem?.completedStageIds || completedStageIds;
    // For stage idx, all prior stages (0 to idx - 1) must be completed
    for (let i = 0; i < idx; i++) {
      const priorId = stages[i]?.id;
      if (!currentCompleted.includes(priorId)) {
        return false;
      }
    }
    return true;
  };

  const handleStageClick = (idx) => {
    if (idx === activeStageIndex) return;

    if (!isStageUnlocked(idx)) {
      toast.error("Please complete the current stage before moving to the next stage.");
      return;
    }

    setActiveStageIndex(idx);
  };

  const handleNextStage = (stageId) => {
    const currentStageId = stageId || stages[activeStageIndex]?.id;
    const currentCompleted = store.getState().stem?.completedStageIds || completedStageIds;

    if (!currentCompleted.includes(currentStageId)) {
      toast.error("Please complete and submit the current stage first.");
      return;
    }

    if (activeStageIndex < stages.length - 1) {
      setActiveStageIndex((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.success("Congratulations! You have completed all stages of this project.");
      if (onBack) {
        onBack();
      }
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Compact Top Header with Project Name & Horizontal Stages Bulletin */}
      <div
        style={{
          background: "var(--white)",
          borderRadius: "10px",
          border: "1px solid var(--line)",
          padding: "10px 14px",
          marginBottom: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        }}
      >
        {/* Project Name in Bold Above Stages */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <h2 style={{ fontSize: "15.5px", fontWeight: "700", color: "var(--ink)", margin: 0 }}>
              {project?.name || "STEM Project"}
            </h2>
            <span
              style={{
                fontSize: "10px",
                fontWeight: "700",
                color: "var(--green-ink)",
                background: "var(--green-soft)",
                padding: "2px 8px",
                borderRadius: "10px",
                letterSpacing: "0.3px",
              }}
            >
              {project?.status || "ACTIVE"}
            </span>
          </div>

          <span
            style={{
              fontSize: "11px",
              color: "var(--mute)",
              fontWeight: "600",
              background: "var(--bg)",
              border: "1px solid var(--line)",
              padding: "2px 8px",
              borderRadius: "8px",
            }}
          >
            {completedStageIds.length} of {stages.length} Stages Completed
          </span>
        </div>

        {/* Stages Row: Back Button on the Left of First Stage + Horizontal Bulletin */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            overflowX: "auto",
            paddingBottom: "2px",
          }}
        >
          {/* Back button at the first stage left side */}
          <button
            type="button"
            onClick={onBack}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: "8px",
              padding: "5px 10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "11.5px",
              fontWeight: "600",
              color: "var(--ink)",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <ArrowLeft size={13} />
            <span>Back</span>
          </button>

          <div style={{ width: "1px", height: "22px", background: "var(--line)", flexShrink: 0 }} />

          {/* Bulletin sequence stages */}
          {loadingStages && stages.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--mute)" }}>
              <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
              <span>Loading stages...</span>
            </div>
          ) : stages.length > 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "nowrap",
              }}
            >
              {stages.map((stg, idx) => {
                const isActive = idx === activeStageIndex;
                const isCompleted = completedStageIds.includes(stg.id);
                const isUnlocked = isStageUnlocked(idx);

                return (
                  <React.Fragment key={stg.id || idx}>
                    <div
                      onClick={() => handleStageClick(idx)}
                      title={!isUnlocked ? "Complete previous stages first" : ""}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        background: isActive
                          ? "var(--blue-soft)"
                          : isCompleted
                          ? "var(--green-tint)"
                          : !isUnlocked
                          ? "var(--bg-2)"
                          : "var(--white)",
                        border: isActive
                          ? "1.5px solid var(--blue-2)"
                          : isCompleted
                          ? "1px solid var(--green-line)"
                          : !isUnlocked
                          ? "1px dashed var(--line-2)"
                          : "1px solid var(--line)",
                        boxShadow: isActive ? "0 0 0 3px var(--blue-line)" : "none",
                        cursor: isUnlocked ? "pointer" : "not-allowed",
                        opacity: isUnlocked ? 1 : 0.65,
                        transition: "all 0.15s ease",
                        flexShrink: 0,
                      }}
                    >
                      {/* Circle badge */}
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          background: isActive
                            ? "var(--blue)"
                            : isCompleted
                            ? "var(--green)"
                            : !isUnlocked
                            ? "var(--line)"
                            : "var(--line)",
                          color: isActive || isCompleted ? "var(--white)" : !isUnlocked ? "var(--faint)" : "var(--mute)",
                          fontSize: "10.5px",
                          fontWeight: "700",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isCompleted ? (
                          <CheckCircle2 size={13} />
                        ) : !isUnlocked ? (
                          <Lock size={10} />
                        ) : (
                          stg.sequenceNo || idx + 1
                        )}
                      </span>

                      {/* Stage Title & Status */}
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          style={{
                            fontSize: "11.5px",
                            fontWeight: isActive ? "700" : "600",
                            color: !isUnlocked && !isCompleted ? "var(--faint)" : "var(--ink)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {stg.name}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "9.5px" }}>
                          <span
                            style={{
                              fontWeight: "600",
                              color: isActive
                                ? "var(--blue-2)"
                                : isCompleted
                                ? "var(--green)"
                                : !isUnlocked
                                ? "var(--faint)"
                                : "var(--mute)",
                            }}
                          >
                            {isActive ? "Active" : isCompleted ? "Completed" : !isUnlocked ? "Locked" : "Pending"}
                          </span>
                          {stg.contentType && (
                            <>
                              <span style={{ color: "var(--line-2)" }}>•</span>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", color: isActive ? "var(--blue-2)" : "var(--mute)" }}>
                                {getTypeIcon(stg.contentType)}
                                <span>{stg.contentType}</span>
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Connector line between horizontal steps */}
                    {idx < stages.length - 1 && (
                      <div
                        style={{
                          width: "12px",
                          height: "2px",
                          background: isCompleted ? "var(--green)" : "var(--line-2)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: "12px", color: "var(--mute)" }}>
              No stages configured for this project.
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Component Content Panel */}
      <div
        style={{
          background: "var(--white)",
          borderRadius: "10px",
          border: "1px solid var(--line)",
          padding: "16px 18px",
          minHeight: "340px",
        }}
      >
        <DynamicStageContent
          stage={activeStage}
          project={project}
          stages={stages}
          isLastStage={activeStageIndex === stages.length - 1}
          isCompleted={completedStageIds.includes(activeStage?.id)}
          onCompleteStage={() => handleCompleteStage(activeStage?.id)}
          onNextStage={handleNextStage}
        />
      </div>
    </div>
  );
}
